/**
 * TA v4.3 — schema helpers + telemetry collection utilities.
 *
 * Shared across phase renderers. Pure functions only — no React state.
 */

// Detect language from a fenced code block inside an arbitrary content string.
// Returns { language, code, leading, trailing } when a fence is found, else null.
// Supports the canonical TA v4.3 fence set: ```java ```sql ```json ```log ```python ```javascript ```typescript ```yaml
const FENCE_RE = /```([a-zA-Z0-9_-]+)?\n([\s\S]*?)```/;
const FENCE_RE_G = /```([a-zA-Z0-9_-]+)?\n([\s\S]*?)```/g;

export function extractFencedCode(text) {
    if (!text || typeof text !== "string") return null;
    const m = text.match(FENCE_RE);
    if (!m) return null;
    const language = (m[1] || "").trim().toLowerCase() || null;
    return {
        language,
        code: (m[2] || "").trimEnd(),
        leading: text.slice(0, m.index).trim(),
        trailing: text.slice(m.index + m[0].length).trim(),
    };
}

// Split a scenario string into an ordered list of segments:
//   [{ kind: 'prose', text }, { kind: 'code', language, code }, ...]
// Handles 0..N fenced blocks interleaved with prose. Empty prose segments are dropped.
export function splitFencedSegments(text) {
    if (!text || typeof text !== "string") return [];
    const segments = [];
    let lastIdx = 0;
    const re = new RegExp(FENCE_RE_G);
    let m;
    while ((m = re.exec(text)) !== null) {
        const before = text.slice(lastIdx, m.index).trim();
        if (before) segments.push({ kind: "prose", text: before });
        const language = (m[1] || "").trim().toLowerCase() || null;
        const code = (m[2] || "").trimEnd();
        segments.push({ kind: "code", language, code });
        lastIdx = m.index + m[0].length;
    }
    const tail = text.slice(lastIdx).trim();
    if (tail) segments.push({ kind: "prose", text: tail });
    return segments;
}

// Normalize Monaco language strings against TA fence ids.
const LANG_MAP = {
    js: "javascript",
    ts: "typescript",
    py: "python",
    yml: "yaml",
    md: "markdown",
    log: "log",
    sh: "shell",
    bash: "shell",
};
export function normalizeMonacoLanguage(raw, fallback) {
    if (!raw) return fallback || "plaintext";
    const key = String(raw).toLowerCase().trim();
    return LANG_MAP[key] || key;
}

// Derive the candidate's declared primary language from the TA v4.3 metadata.
// Used as fallback when a code block has no fence.
export function primaryLanguageFromMeta(template) {
    const meta = template?.content?.metadata || {};
    return (meta.primary_language || meta.language || "plaintext").toLowerCase();
}

// Word count — visible-word boundary.
export function wordCount(text) {
    if (!text) return 0;
    return text.trim().split(/\s+/).filter(Boolean).length;
}

// Format mm:ss
export function fmtClock(secondsRemaining) {
    const s = Math.max(0, Math.floor(secondsRemaining));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

// Seconds remaining from a server-issued ISO deadline.
export function secondsUntil(iso) {
    if (!iso) return 0;
    const d = new Date(iso).getTime();
    if (Number.isNaN(d)) return 0;
    return Math.floor((d - Date.now()) / 1000);
}

// Construct a fresh telemetry record for a response_field_id.
// Returns the record + mutating helpers; the component owns the ref.
export function newTelemetryState(responseFieldId, itemId) {
    return {
        response_field_id: responseFieldId,
        item_id: itemId,
        text: "",
        completion_seconds: 0,
        time_to_first_keystroke_ms: null,
        paste_events: [],
        focus_events: [],
        keystroke_count: 0,
        word_count: 0,
        _focus_started_at: null,
        _first_keystroke_logged: false,
        _mounted_at: Date.now(),
    };
}

// Detect whether a Phase 3 response_field is "the communication" — the bit the candidate
// must literally write as if sending to a stakeholder. We deliberately exclude meta /
// follow-up fields (e.g. "What did you choose NOT to put in the email") to avoid
// false-positive EMAIL DRAFT badges on commentary prompts.
const COMM_LABEL_RE = /\b(email|message|draft|communication)\b/i;
const COMM_PROMPT_RE = /\bwrite the (email|message)|draft the communication\b/i;
const META_LABEL_RE = /^\s*(what|why|how|explain|describe|when|did you)\b/i;
export function isCommunicationField(field, question) {
    if (!field) return false;
    const label = field.label || "";
    if (META_LABEL_RE.test(label)) return false;
    if (COMM_LABEL_RE.test(label)) return true;
    // Prompt-level fallback only fires when the label is generic (e.g. "Primary Response")
    if (COMM_LABEL_RE.test(label)) return true;
    const scenario = (question?.scenario || "") + " " + (question?.primary_question || "");
    return COMM_PROMPT_RE.test(scenario) && !META_LABEL_RE.test(label);
}
